import React, { useState, useEffect } from 'react';
import {
  DownOutlined,
  UploadOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  AppstoreOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  ScissorOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { Upload, Button, Input, Card, Tag, Select, Modal, Form, message } from 'antd';

const { Option } = Select;

const MetaAdFormatMedia = ({ campaignData, onUpdate, onNext }) => {
  const [adFormat, setAdFormat] = useState(campaignData.metaAds?.adFormat || 'single');
  const [mediaFiles, setMediaFiles] = useState(campaignData.metaAds?.mediaFiles || []);
  const [carouselCards, setCarouselCards] = useState(campaignData.metaAds?.carouselCards || []);
  const [collectionCover, setCollectionCover] = useState(campaignData.metaAds?.collectionCover || null);
  const [selectedCatalog, setSelectedCatalog] = useState(campaignData.metaAds?.selectedCatalog || '');
  const [showBestPerformingFirst, setShowBestPerformingFirst] = useState(campaignData.metaAds?.showBestPerformingFirst || false);
  const [cropSettings] = useState(campaignData.metaAds?.cropSettings || {});
  const [showAddCatalogModal, setShowAddCatalogModal] = useState(false);
  const [customCatalogs, setCustomCatalogs] = useState(campaignData.metaAds?.customCatalogs || []);
  const [newCatalogForm] = Form.useForm();

  // Mock product catalogs (most used by Meta)
  const defaultCatalogs = [
    { value: 'catalog_1', label: 'Fashion Store Catalog', type: 'fashion', products: 1250 },
    { value: 'catalog_2', label: 'Electronics Catalog', type: 'electronics', products: 890 },
    { value: 'catalog_3', label: 'Home & Garden Catalog', type: 'home', products: 567 },
    { value: 'catalog_4', label: 'Beauty & Personal Care', type: 'beauty', products: 432 },
    { value: 'catalog_5', label: 'Sports & Outdoors', type: 'sports', products: 321 },
    { value: 'catalog_6', label: 'Professional Services', type: 'services', products: 234 },
    { value: 'catalog_7', label: 'Healthcare Services', type: 'healthcare', products: 189 },
    { value: 'catalog_8', label: 'Education & Training', type: 'education', products: 156 },
    { value: 'catalog_9', label: 'Business Consulting', type: 'business', products: 143 },
    { value: 'catalog_10', label: 'Home Services', type: 'home_services', products: 128 },
    { value: 'catalog_11', label: 'Automotive Services', type: 'automotive_services', products: 97 },
    { value: 'catalog_12', label: 'Financial Services', type: 'financial', products: 85 },
    { value: 'catalog_13', label: 'Legal Services', type: 'legal', products: 72 },
    { value: 'catalog_14', label: 'Marketing & Design', type: 'marketing', products: 68 },
    { value: 'catalog_15', label: 'Technology Services', type: 'tech_services', products: 54 },
  ];

  // Combine default and custom catalogs
  const allCatalogs = [...defaultCatalogs, ...customCatalogs];
  const productCatalogs = allCatalogs.map(catalog => ({
    value: catalog.value,
    label: catalog.label
  }));

  const formatOptions = [
    {
      id: 'single',
      title: 'Single Image or Video',
      description: 'One compelling image or video that tells your story',
      icon: <PictureOutlined />,
    },
    {
      id: 'carousel',
      title: 'Carousel (2–10 cards)',
      description: 'Multiple images or videos in a swipeable format',
      icon: <AppstoreOutlined />,
    },
    {
      id: 'collection',
      title: 'Collection (cover + product grid)',
      description: 'Showcase multiple products with a beautiful cover',
      icon: <ShopOutlined />,
    },
  ];

  const catalogTypes = [
    { value: 'fashion', label: 'Fashion & Apparel' },
    { value: 'electronics', label: 'Electronics & Tech' },
    { value: 'home', label: 'Home & Garden' },
    { value: 'beauty', label: 'Beauty & Personal Care' },
    { value: 'sports', label: 'Sports & Outdoors' },
    { value: 'food', label: 'Food & Beverage' },
    { value: 'health', label: 'Health & Wellness' },
    { value: 'toys', label: 'Toys & Games' },
    { value: 'books', label: 'Books & Media' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'services', label: 'Professional Services' },
    { value: 'healthcare', label: 'Healthcare Services' },
    { value: 'education', label: 'Education & Training' },
    { value: 'business', label: 'Business Consulting' },
    { value: 'home_services', label: 'Home Services' },
    { value: 'automotive_services', label: 'Automotive Services' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'legal', label: 'Legal Services' },
    { value: 'marketing', label: 'Marketing & Design' },
    { value: 'tech_services', label: 'Technology Services' },
    { value: 'other', label: 'Other' },
  ];

  const handleAddCatalog = async (values) => {
    const newCatalog = {
      value: `custom_${Date.now()}`,
      label: values.name,
      type: values.type,
      products: parseInt(values.products) || 0,
      isCustom: true,
    };
    
    setCustomCatalogs([...customCatalogs, newCatalog]);
    setSelectedCatalog(newCatalog.value);
    setShowAddCatalogModal(false);
    newCatalogForm.resetFields();
    message.success(`Added catalog: ${values.name}`);
  };

  const removeCustomCatalog = (catalogValue) => {
    setCustomCatalogs(customCatalogs.filter(c => c.value !== catalogValue));
    if (selectedCatalog === catalogValue) {
      setSelectedCatalog('');
    }
    message.success('Custom catalog removed');
  };

  const handleFormatChange = (format) => {
    setAdFormat(format);
    // Reset format-specific data when changing formats
    if (format !== 'carousel') {
      setCarouselCards([]);
      setShowBestPerformingFirst(false);
    }
    if (format !== 'collection') {
      setCollectionCover(null);
      setSelectedCatalog('');
    }
  };

  const handleMediaUpload = (file) => {
    // Mock upload logic
    const newFile = {
      id: Date.now(),
      name: file.name,
      type: file.type.startsWith('video/') ? 'video' : 'image',
      url: URL.createObjectURL(file),
      size: file.size,
      status: 'done',
    };

    if (adFormat === 'single') {
      setMediaFiles([newFile]);
    } else if (adFormat === 'collection') {
      setCollectionCover(newFile);
    }

    return false; // Prevent default upload behavior
  };

  const addCarouselCard = () => {
    if (carouselCards.length >= 10) {
      message.warning('Maximum 10 cards allowed in carousel');
      return;
    }

    const newCard = {
      id: Date.now(),
      headline: '',
      description: '',
      url: '',
      media: null,
    };

    setCarouselCards([...carouselCards, newCard]);
  };

  const updateCarouselCard = (cardId, field, value) => {
    setCarouselCards(cards =>
      cards.map(card =>
        card.id === cardId ? { ...card, [field]: value } : card
      )
    );
  };

  const removeCarouselCard = (cardId) => {
    setCarouselCards(cards => cards.filter(card => card.id !== cardId));
  };

  const handleCardMediaUpload = (cardId, file) => {
    const newFile = {
      id: Date.now(),
      name: file.name,
      type: file.type.startsWith('video/') ? 'video' : 'image',
      url: URL.createObjectURL(file),
      size: file.size,
      status: 'done',
    };

    updateCarouselCard(cardId, 'media', newFile);
    return false;
  };

  const validateCurrentFormat = () => {
    if (adFormat === 'single' && mediaFiles.length === 0) {
      message.warning('Please upload an image or video for single format');
      return false;
    }

    if (adFormat === 'carousel') {
      if (carouselCards.length < 2) {
        message.warning('Carousel requires at least 2 cards');
        return false;
      }
      const incompleteCards = carouselCards.filter(card => !card.media || !card.headline.trim());
      if (incompleteCards.length > 0) {
        message.warning('All carousel cards must have media and headline');
        return false;
      }
    }

    if (adFormat === 'collection') {
      if (!collectionCover) {
        message.warning('Please upload a cover image for collection');
        return false;
      }
      if (!selectedCatalog) {
        message.warning('Please select a product catalog');
        return false;
      }
    }

    return true;
  };

  // Keep parent data in sync as fields change (single-page campaign view)
  useEffect(() => {
    onUpdate({
      metaAds: {
        adFormat,
        mediaFiles,
        carouselCards,
        collectionCover,
        selectedCatalog,
        showBestPerformingFirst,
        cropSettings,
        customCatalogs,
      }
    });
  }, [adFormat, mediaFiles, carouselCards, collectionCover, selectedCatalog, showBestPerformingFirst, cropSettings, customCatalogs]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    if (!validateCurrentFormat()) {
      return;
    }

    onUpdate({
      metaAds: {
        ...campaignData.metaAds,
        adFormat,
        mediaFiles,
        carouselCards,
        collectionCover,
        selectedCatalog,
        showBestPerformingFirst,
        cropSettings,
        customCatalogs,
      }
    });
    onNext();
  };

  const renderSingleFormat = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px', alignItems: 'start' }}>
      {/* Left: Upload area */}
      <div style={{
        background: '#f8fafc',
        border: '2px dashed var(--paper-300)',
        borderRadius: '8px',
        padding: '40px',
        textAlign: 'center',
        minHeight: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Upload
          accept="image/*,video/*"
          showUploadList={false}
          beforeUpload={handleMediaUpload}
          disabled={mediaFiles.length > 0}
        >
          <div style={{ cursor: mediaFiles.length > 0 ? 'default' : 'pointer' }}>
            <UploadOutlined style={{ fontSize: '48px', color: 'var(--paper-300)', marginBottom: '16px' }} />
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              {mediaFiles.length > 0 ? 'Media Uploaded' : 'Drag & drop your media here'}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
              {mediaFiles.length > 0
                ? 'Click to replace'
                : 'Or click to browse files (JPG, PNG, MP4, MOV)'}
            </div>
          </div>
        </Upload>
      </div>

      {/* Right: Files list + library button */}
      <div>
        {mediaFiles.length > 0 ? (
          <>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              Uploaded Media:
            </div>
            {mediaFiles.map(file => (
              <Card key={file.id} size="small" style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {file.type === 'video' ? <VideoCameraOutlined /> : <PictureOutlined />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500 }}>{file.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>
                      {file.type.toUpperCase()} • {(file.size / 1024 / 1024).toFixed(1)}MB
                    </div>
                  </div>
                  <Tag color={file.type === 'video' ? 'blue' : 'green'}>
                    {file.type === 'video' ? 'VIDEO' : 'IMAGE'}
                  </Tag>
                </div>
              </Card>
            ))}
          </>
        ) : (
          <div style={{
            background: '#f9fafb', border: '1px solid var(--paper-300)', borderRadius: '8px',
            padding: '24px', textAlign: 'center', color: '#9ca3af', minHeight: '120px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <PictureOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
            <div style={{ fontSize: '13px' }}>No media uploaded yet</div>
          </div>
        )}
        <Button type="link" icon={<EyeOutlined />} style={{ marginTop: '8px', paddingLeft: 0 }}>
          Add from Meta Media Library
        </Button>
      </div>
    </div>
  );

  const renderCarouselFormat = () => (
    <div style={{ marginTop: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)', marginBottom: '12px' }}>
          Create 2-10 cards for your carousel. Each card can have its own image/video, headline, and description.
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          padding: '12px',
          background: '#f8fafc',
          borderRadius: '6px',
        }}>
          <span style={{ fontSize: '14px', fontWeight: 500 }}>
            Cards: {carouselCards.length}/10
          </span>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={addCarouselCard}
            disabled={carouselCards.length >= 10}
          >
            Add Card
          </Button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px',
            background: '#f0f9ff',
            borderRadius: '6px',
            border: '1px solid var(--ink)',
          }}>
            <input
              type="checkbox"
              checked={showBestPerformingFirst}
              onChange={(e) => setShowBestPerformingFirst(e.target.checked)}
              style={{ margin: 0 }}
            />
            <span style={{ fontSize: '14px' }}>
              Automatically show the best performing card first
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: '16px' }}>
        {carouselCards.map((card, index) => (
          <Card
            key={card.id}
            size="small"
            title={`Card ${index + 1}`}
            extra={
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeCarouselCard(card.id)}
                size="small"
              />
            }
          >
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '16px' }}>
              {/* Media Upload */}
              <div>
                <div style={{
                  width: '180px',
                  height: '120px',
                  border: '2px dashed var(--paper-300)',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background: '#f9fafb',
                }}>
                  {card.media ? (
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      {card.media.type === 'video' ? (
                        <video
                          src={card.media.url}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                          controls={false}
                        />
                      ) : (
                        <img
                          src={card.media.url}
                          alt="Card media"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                        />
                      )}
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          background: 'rgba(0,0,0,0.5)',
                          color: 'white',
                        }}
                      />
                    </div>
                  ) : (
                    <Upload
                      accept="image/*,video/*"
                      showUploadList={false}
                      beforeUpload={(file) => handleCardMediaUpload(card.id, file)}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <PictureOutlined style={{ fontSize: '24px', color: 'var(--paper-300)' }} />
                        <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '4px' }}>
                          Upload Media
                        </div>
                      </div>
                    </Upload>
                  )}
                </div>
              </div>

              {/* Card Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                    Headline (40 chars max)
                  </label>
                  <Input
                    value={card.headline}
                    onChange={(e) => updateCarouselCard(card.id, 'headline', e.target.value)}
                    maxLength={40}
                    placeholder="Enter card headline..."
                    size="small"
                  />
                  <div style={{ fontSize: '10px', color: 'var(--ink-200)', marginTop: '2px' }}>
                    {card.headline.length}/40 characters
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                    Description (20 chars max)
                  </label>
                  <Input
                    value={card.description}
                    onChange={(e) => updateCarouselCard(card.id, 'description', e.target.value)}
                    maxLength={20}
                    placeholder="Enter card description..."
                    size="small"
                  />
                  <div style={{ fontSize: '10px', color: 'var(--ink-200)', marginTop: '2px' }}>
                    {card.description.length}/20 characters
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                    URL (optional)
                  </label>
                  <Input
                    value={card.url}
                    onChange={(e) => updateCarouselCard(card.id, 'url', e.target.value)}
                    placeholder="https://..."
                    size="small"
                  />
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderCollectionFormat = () => (
    <div style={{ marginTop: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
          Cover Media
        </h4>

        <div style={{
          background: '#f8fafc',
          border: '2px dashed var(--paper-300)',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          marginBottom: '16px',
        }}>
          <Upload
            accept="image/*,video/*"
            showUploadList={false}
            beforeUpload={handleMediaUpload}
            disabled={!!collectionCover}
          >
            <div style={{ cursor: collectionCover ? 'default' : 'pointer' }}>
              <UploadOutlined style={{ fontSize: '48px', color: 'var(--paper-300)', marginBottom: '16px' }} />
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                {collectionCover ? 'Cover Uploaded' : 'Upload Cover Media'}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
                {collectionCover
                  ? 'Click to replace'
                  : 'JPG, PNG, MP4, MOV (1080×1080 or 1080×1920)'}
              </div>
            </div>
          </Upload>
        </div>

        {collectionCover && (
          <Card size="small">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {collectionCover.type === 'video' ? <VideoCameraOutlined /> : <PictureOutlined />}
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{collectionCover.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>
                  Cover • {(collectionCover.size / 1024 / 1024).toFixed(1)}MB
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Product Catalog + Media Specifications side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
        
        {/* Product Catalog */}
        <div style={{ background: '#fff', border: '1px solid var(--paper-300)', borderRadius: '12px', padding: '20px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
            Product Catalog
          </h4>

          <Select
            value={selectedCatalog}
            onChange={setSelectedCatalog}
            placeholder="Select a product catalog"
            style={{ width: '100%' }}
            size="large"
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => setShowAddCatalogModal(true)}
                    style={{ width: '100%' }}
                  >
                    Add Custom Catalog
                  </Button>
                </div>
              </>
            )}
          >
            {productCatalogs.map(catalog => (
              <Option key={catalog.value} value={catalog.value}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{catalog.label}</span>
                  {allCatalogs.find(c => c.value === catalog.value)?.isCustom && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustomCatalog(catalog.value);
                      }}
                    />
                  )}
                </div>
              </Option>
            ))}
          </Select>

          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '8px' }}>
            Choose the catalog containing products to display in your collection. <br />
            Can't find yours? Click "Add Custom Catalog" to create one.
          </div>
        </div>

        {/* Media Specifications (moved here from below) */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
            Media Specifications
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                Images
              </div>
              <div style={{ fontSize: '12px', color: 'var(--ink-200)', lineHeight: 1.4 }}>
                • 1080×1080 (1:1) or 1080×1920 (9:16)<br/>
                • JPG/PNG format<br/>
                • Max 30MB
              </div>
            </div>

            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                Videos
              </div>
              <div style={{ fontSize: '12px', color: 'var(--ink-200)', lineHeight: 1.4 }}>
                • MP4/MOV format<br/>
                • Max 4GB<br/>
                • 1–240 seconds
              </div>
            </div>
          </div>

          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ScissorOutlined style={{ color: 'var(--ink-200)' }} />
              <span style={{ fontSize: '12px', color: 'var(--ink-200)' }}>
                Images and videos will be automatically cropped and adjusted for each placement (Feed, Stories, Reels)
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  return (
    <div className="step-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 100px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
        }}>
          <PictureOutlined style={{ fontSize: '32px', color: '#111827' }} />
        </div>

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: '#000',
          marginBottom: '12px',
          textAlign: 'center'
        }}>
          Ad Format & Media
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'var(--ink-200)',
          marginBottom: 0,
          textAlign: 'center'
        }}>
          Choose your ad format and upload creative assets
        </p>
      </div>

      {/* Format Selection */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>
          Format Selection
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {formatOptions.map(format => (
            <div
              key={format.id}
              onClick={() => handleFormatChange(format.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '20px',
                background: adFormat === format.id ? '#f0f9ff' : '#fff',
                border: adFormat === format.id ? '2px solid var(--ink)' : '1px solid var(--paper-300)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: adFormat === format.id ? 'var(--ink)' : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                color: adFormat === format.id ? '#fff' : 'var(--ink-200)',
              }}>
                {format.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                  {format.title}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
                  {format.description}
                </div>
              </div>
              {adFormat === format.id && (
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'var(--ink)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}>
                  ✓
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Format-Specific Content */}
      {adFormat === 'single' && renderSingleFormat()}
      {adFormat === 'carousel' && renderCarouselFormat()}
      {adFormat === 'collection' && renderCollectionFormat()}

      {/* Add Catalog Modal */}
      <Modal
        title="Add Custom Catalog"
        open={showAddCatalogModal}
        onCancel={() => {
          setShowAddCatalogModal(false);
          newCatalogForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={newCatalogForm}
          layout="vertical"
          onFinish={handleAddCatalog}
        >
          <Form.Item
            name="name"
            label="Catalog Name"
            rules={[{ required: true, message: 'Please enter catalog name' }]}
          >
            <Input placeholder="e.g., My Store Catalog" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Catalog Type"
            rules={[{ required: true, message: 'Please select catalog type' }]}
          >
            <Select placeholder="Select catalog type">
              {catalogTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="products"
            label="Number of Products"
            rules={[
              { required: true, message: 'Please enter number of products' },
              { type: 'number', min: 1, message: 'Must be at least 1 product' }
            ]}
          >
            <Input
              type="number"
              placeholder="e.g., 500"
              min={1}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowAddCatalogModal(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Catalog
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid var(--paper-300)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10
      }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
          Building: Ad Format & Media
        </div>
        <button
          onClick={handleContinue}
          style={{
            padding: '12px 32px',
            background: 'var(--ink-200)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#4b5563';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--ink-200)';
          }}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default MetaAdFormatMedia;
